import styled from '@emotion/styled'
import gql from 'graphql-tag'
import React, { useState } from 'react'
import { useMutation, useQuery } from '../../../api/graphql'
import SocialPostEditModal from './SocialPostEditModal'

const SOCIAL_POSTS_QUERY = gql`
  query SocialPosts {
    currentUser {
      id
      organizationMemberships {
        role
        organization {
          id
          socialPosts {
            id
            caption
            status
            platform
            scheduledFor
            postedAt
            platformPostUrl
            errorMessage
            retryCount
            eventTitle
            socialAccount {
              id
              platformAccountName
            }
          }
        }
      }
    }
  }
`

const CANCEL_MUTATION = gql`
  mutation SocialPostCancel($id: ID!) {
    socialPostCancel(id: $id) {
      socialPost {
        id
        status
      }
      errors {
        attribute
        message
      }
    }
  }
`

const RETRY_MUTATION = gql`
  mutation SocialPostRetry($id: ID!) {
    socialPostRetry(id: $id) {
      socialPost {
        id
        status
      }
      errors {
        attribute
        message
      }
    }
  }
`

type SocialPost = {
  id: string
  caption: string
  status: string
  platform: string
  scheduledFor: string | null
  postedAt: string | null
  platformPostUrl: string | null
  errorMessage: string | null
  retryCount: number
  eventTitle: string
  socialAccount: {
    id: string
    platformAccountName: string | null
  }
}

type Props = {
  organizationId: string
}

const SocialPostsManager: React.FC<Props> = ({ organizationId }) => {
  const { data, loading, refetch } = useQuery(SOCIAL_POSTS_QUERY, {
    variables: { organizationId },
  })
  const [cancelPost] = useMutation(CANCEL_MUTATION)
  const [retryPost] = useMutation(RETRY_MUTATION)
  const [editingPost, setEditingPost] = useState<SocialPost | null>(null)

  const allPosts: SocialPost[] =
    data?.currentUser?.organizationMemberships
      ?.find((m: { organization: { id: string } }) => m.organization.id === organizationId)
      ?.organization?.socialPosts || []

  const upcomingPosts = allPosts.filter(p => ['pending', 'scheduled', 'posting'].includes(p.status))
  const recentPosts = allPosts.filter(p => ['posted', 'failed', 'cancelled'].includes(p.status))

  const handleCancel = async (postId: string) => {
    if (!window.confirm('Cancel this scheduled post?')) return
    await cancelPost({ variables: { id: postId } })
    refetch()
  }

  const handleRetry = async (postId: string) => {
    await retryPost({ variables: { id: postId } })
    refetch()
  }

  const platformLabel = (platform: string) => {
    switch (platform) {
      case 'facebook_page':
        return 'Facebook'
      case 'instagram_business':
        return 'Instagram'
      default:
        return platform
    }
  }

  const statusBadge = (status: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      pending: { bg: '#fef3c7', text: '#92400e' },
      scheduled: { bg: '#dbeafe', text: '#1e40af' },
      posting: { bg: '#e0e7ff', text: '#3730a3' },
      posted: { bg: '#dcfce7', text: '#166534' },
      failed: { bg: '#fee2e2', text: '#991b1b' },
      cancelled: { bg: '#f3f4f6', text: '#6b7280' },
    }
    const c = colors[status] || colors.pending
    return (
      <StatusBadge bg={c.bg} textColor={c.text}>
        {status}
      </StatusBadge>
    )
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return ''
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  if (loading) {
    return <LoadingText>Loading social posts...</LoadingText>
  }

  return (
    <Container>
      {editingPost && (
        <SocialPostEditModal
          post={editingPost}
          onClose={() => setEditingPost(null)}
          onSaved={() => {
            setEditingPost(null)
            refetch()
          }}
        />
      )}

      <Section>
        <SectionTitle>Upcoming</SectionTitle>
        {upcomingPosts.length === 0 ? (
          <EmptyText>No upcoming social posts. Share an event to get started.</EmptyText>
        ) : (
          <PostsList>
            {upcomingPosts.map(post => (
              <PostRow key={post.id}>
                <PostInfo>
                  <PostEventName>{post.eventTitle}</PostEventName>
                  <PostMeta>
                    <PlatformBadge platform={post.platform}>{platformLabel(post.platform)}</PlatformBadge>
                    <span>{post.socialAccount.platformAccountName}</span>
                    {post.scheduledFor && <span>Scheduled: {formatDate(post.scheduledFor)}</span>}
                  </PostMeta>
                  <PostCaption>{post.caption.substring(0, 100)}{post.caption.length > 100 ? '...' : ''}</PostCaption>
                </PostInfo>
                <PostActions>
                  {statusBadge(post.status)}
                  {['pending', 'scheduled'].includes(post.status) && (
                    <>
                      <ActionButton onClick={() => setEditingPost(post)}>Edit</ActionButton>
                      <ActionButton onClick={() => handleCancel(post.id)} variant="danger">
                        Cancel
                      </ActionButton>
                    </>
                  )}
                </PostActions>
              </PostRow>
            ))}
          </PostsList>
        )}
      </Section>

      <Section>
        <SectionTitle>Recent</SectionTitle>
        {recentPosts.length === 0 ? (
          <EmptyText>No recent posts yet.</EmptyText>
        ) : (
          <PostsList>
            {recentPosts.map(post => (
              <PostRow key={post.id}>
                <PostInfo>
                  <PostEventName>{post.eventTitle}</PostEventName>
                  <PostMeta>
                    <PlatformBadge platform={post.platform}>{platformLabel(post.platform)}</PlatformBadge>
                    <span>{post.socialAccount.platformAccountName}</span>
                    {post.postedAt && <span>{formatDate(post.postedAt)}</span>}
                  </PostMeta>
                  {post.status === 'failed' && post.errorMessage && <ErrorText>{post.errorMessage}</ErrorText>}
                </PostInfo>
                <PostActions>
                  {statusBadge(post.status)}
                  {post.status === 'posted' && post.platformPostUrl && (
                    <ViewLink href={post.platformPostUrl} target="_blank" rel="noopener noreferrer">
                      View Post
                    </ViewLink>
                  )}
                  {post.status === 'failed' && post.retryCount < 3 && (
                    <ActionButton onClick={() => handleRetry(post.id)}>Retry</ActionButton>
                  )}
                </PostActions>
              </PostRow>
            ))}
          </PostsList>
        )}
      </Section>
    </Container>
  )
}

export default SocialPostsManager

const Container = styled.div``

const Section = styled.div`
  margin-bottom: 32px;
`

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #102638;
  margin: 0 0 16px;
`

const PostsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const PostRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`

const PostInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const PostEventName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #102638;
  margin-bottom: 4px;
`

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
  flex-wrap: wrap;
`

const PostCaption = styled.div`
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.4;
`

const PostActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const StatusBadge = styled.span<{ bg: string; textColor: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  background: ${({ bg }) => bg};
  color: ${({ textColor }) => textColor};
`

const PlatformBadge = styled.span<{ platform: string }>`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ platform }) => (platform === 'facebook_page' ? '#e7f0ff' : '#fce7f3')};
  color: ${({ platform }) => (platform === 'facebook_page' ? '#1877f2' : '#c026d3')};
`

const ActionButton = styled.button<{ variant?: string }>`
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid ${({ variant }) => (variant === 'danger' ? '#fca5a5' : '#d1d5db')};
  background: white;
  color: ${({ variant }) => (variant === 'danger' ? '#dc2626' : '#374151')};

  &:hover {
    background: ${({ variant }) => (variant === 'danger' ? '#fef2f2' : '#f9fafb')};
  }
`

const ViewLink = styled.a`
  font-size: 12px;
  font-weight: 500;
  color: #1877f2;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const ErrorText = styled.div`
  font-size: 12px;
  color: #dc2626;
  margin-top: 4px;
`

const EmptyText = styled.p`
  font-size: 14px;
  color: #9ca3af;
  text-align: center;
  padding: 32px 0;
`

const LoadingText = styled.p`
  color: #6b7280;
  text-align: center;
  padding: 48px 0;
`
